import { useState, useEffect } from 'react'
import getSkinnyBanners from 'lib/api/skinny-banners'
import { isAppWebView } from 'lib/utils/common/commonUtils'

// Several unrelated components (layout skinny banner, home editorial slides,
// PDP image carousel) independently need this same global banner list on the
// same page load. Share one in-flight/resolved fetch per customer_groups key
// for a short window so they don't each trigger their own network request.
const CACHE_TTL_MS = 30000
const pendingRequests = {}

function fetchSkinnyBanners(customer_groups) {
  const cacheKey = JSON.stringify(customer_groups || null)

  if (!pendingRequests[cacheKey]) {
    pendingRequests[cacheKey] = getSkinnyBanners()
      .then((res) => res?.['skinny-banners'] || [])
      .catch((error) => {
        delete pendingRequests[cacheKey]
        throw error
      })

    setTimeout(() => {
      delete pendingRequests[cacheKey]
    }, CACHE_TTL_MS)
  }

  return pendingRequests[cacheKey]
}

export function clearSkinnyBannersCache() {
  Object.keys(pendingRequests).forEach((key) => {
    delete pendingRequests[key]
  })
}

function useSkinnyBanners(isGuestMode, translation, customer_groups) {
  const [banners, setBanners] = useState([])
  const [isFetched, setIsFetched] = useState(false)

  useEffect(() => {
    let ignore = false

    async function loadBanners() {
      if (isAppWebView()) {
        setIsFetched(true)
        return
      }

      try {
        const result = await fetchSkinnyBanners(customer_groups)
        if (!ignore) {
          setBanners(result)
        }
      } catch (error) {
        // continue regardless of error
      } finally {
        if (!ignore) {
          setIsFetched(true)
        }
      }
    }

    loadBanners()

    return () => {
      ignore = true
    }
    // translation (t) is not referentially stable across renders and would
    // cause this effect to refetch on every render if included here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer_groups, isGuestMode])

  return [banners, isFetched]
}

export default useSkinnyBanners
